import { useCallback } from 'react';
import { productData } from '../data/mockData';
import { getSizeLimit } from '../pages/QuoteCalculator/quoteConstants';

/**
 * Encapsulates the read-only product-catalogue derivations the quote
 * calculator was previously inlining six different ways.
 *
 * All returned helpers are pure functions over the current selection +
 * `productData`, so callers don't need to memoize them — referential
 * stability is preserved by `useCallback` on the hook side.
 *
 * Inputs:
 *   - selectedProduct  : top-level category id (e.g. 'stickers')
 *   - selectedFormat   : subcategory id (e.g. 'die-cut-singles')
 *   - selectedMaterial : material id within the subcategory
 *
 * Returned helpers:
 *   - getAvailableMaterials() -> Material[]   — materials valid for current
 *                                                 (product, format) combo
 *   - getQuantityConfig()     -> Object|null — quantityType + presets,
 *                                                 prefers material-specific
 *                                                 config when set (e.g.
 *                                                 Classic Tattoos overrides)
 *   - requiresCustomQuote()   -> bool        — subcategory itself is a
 *                                                 customQuote stub
 *   - requiresCustomQuoteAtQuantity() -> bool — quantityType=='customQuote'
 *   - checkSizeLimit(width, height)
 *                              -> {valid, maxW?, maxH?, message?}
 */
export function useProductSelection({
  selectedProduct,
  selectedFormat,
  selectedMaterial,
}) {
  const getCategory = useCallback(
    () => productData.categories.find((c) => c.id === selectedProduct) || null,
    [selectedProduct],
  );

  const getSubcategory = useCallback(() => {
    const category = getCategory();
    if (!category) return null;
    return category.subcategories.find((s) => s.id === selectedFormat) || null;
  }, [getCategory, selectedFormat]);

  const getAvailableMaterials = useCallback(() => {
    const sub = getSubcategory();
    return sub?.materials || [];
  }, [getSubcategory]);

  const getQuantityConfig = useCallback(() => {
    const sub = getSubcategory();
    if (!sub) return null;
    if (selectedMaterial) {
      const m = (sub.materials || []).find((mat) => mat.id === selectedMaterial);
      if (m && m.quantityType) return m;
    }
    return sub;
  }, [getSubcategory, selectedMaterial]);

  const requiresCustomQuote = useCallback(() => {
    const sub = getSubcategory();
    return Boolean(sub?.customQuote);
  }, [getSubcategory]);

  const requiresCustomQuoteAtQuantity = useCallback(
    () => getQuantityConfig()?.quantityType === 'customQuote',
    [getQuantityConfig],
  );

  const checkSizeLimit = useCallback(
    (width, height) => {
      if (!selectedProduct || !selectedFormat || !width || !height) {
        return { valid: true };
      }
      const w = parseFloat(width);
      const h = parseFloat(height);
      const productLimit = getSizeLimit(selectedProduct, selectedFormat);
      if (!productLimit) return { valid: true };

      const exceeds = w > productLimit.maxW || h > productLimit.maxH;
      return {
        valid: !exceeds,
        maxW: productLimit.maxW,
        maxH: productLimit.maxH,
        message: exceeds
          ? `Maximum size for this product is ${productLimit.maxW}" x ${productLimit.maxH}". Please request a custom quote for larger sizes.`
          : null,
      };
    },
    [selectedProduct, selectedFormat],
  );

  return {
    getAvailableMaterials,
    getQuantityConfig,
    requiresCustomQuote,
    requiresCustomQuoteAtQuantity,
    checkSizeLimit,
  };
}

export default useProductSelection;
